/**
 * @file components/organic-materials/OrganicMaterialReferenceLinks.tsx
 * @description 有機資材允用判定「外部查詢連結」清單（/organic-materials 與稽核任務審查卡片共用）。
 *
 * 2026-09-01 回報序4：連結原本寫死在兩個頁面，客戶詢問如何修改。現改讀字典
 * GET /api/settings/organic-material-links?activeOnly=true，由管理組於
 * 「系統設置 → 業務字典 — 有機資材查詢連結」維護；讀取失敗（離線 / 無權限）時回落預設清單。
 */

import { Space, Typography } from 'antd';
import { ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DEFAULT_ORGANIC_MATERIAL_LINKS } from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';

/** 字典列表回應（只取渲染需要的欄位） */
interface LinkListResponse {
  items: { id: number; name: string; url: string }[];
}

/** 外部查詢連結清單 */
export function OrganicMaterialReferenceLinks() {
  const { data, isError } = useQuery({
    queryKey: ['settings-lookup', 'organic-material-links', { activeOnly: true }],
    queryFn: () =>
      apiClient.get<LinkListResponse>('/api/settings/organic-material-links?activeOnly=true'),
    staleTime: 5 * 60 * 1000,
  });

  // 讀取失敗時回落預設清單，確保離線或權限不足時稽核員仍可使用
  const links = isError
    ? DEFAULT_ORGANIC_MATERIAL_LINKS.map((l) => ({ key: l.url, name: l.name, url: l.url }))
    : (data?.items ?? []).map((l) => ({ key: String(l.id), name: l.name, url: l.url }));

  if (!isError && data && links.length === 0) {
    return <Typography.Text type="secondary">尚未設定查詢連結</Typography.Text>;
  }

  return (
    <Space direction="vertical" size={4}>
      {links.map((link) => (
        <Typography.Link key={link.key} href={link.url} target="_blank" rel="noopener noreferrer">
          <ExternalLink size={13} style={{ marginRight: 6, verticalAlign: -2 }} />
          {link.name}
        </Typography.Link>
      ))}
    </Space>
  );
}
